import { Drafters } from './drafters'
import { SavedDraft } from './saved-draft'

export interface LeagueSettingsPositionSettings {
  qb: number
  rb: number
  wr: number
  te: number
  k: number
  dst: number
  flex: number
  op: number // superflex position
  rec_flex: number
  wrrb_flex: number
  bench: number
  de: number
  dt: number
  dl: number
  lb: number
  s: number
  cb: number
  db: number
  def_util: number
}

export interface LeagueSettingsPositionLimitSettings {
  limit_qb?: number
  limit_wr?: number
  limit_rb?: number
  limit_te?: number
  limit_k?: number
  limit_dst?: number
}

export interface LeagueSettingsScoringSettings {
  pass_td: number
  rush_td: number
  recv_td: number
  pass_int: number
  pass_yds1: number
  pass_yds5: number
  pass_yds10: number
  pass_yds20: number
  pass_yds25: number
  pass_yds50: number
  pass_yds100: number
  rush_yds1: number
  rush_yds5: number
  rush_yds10: number
  rush_yds20: number
  rush_yds25: number
  rush_yds50: number
  rush_yds100: number
  recv_yds1: number
  recv_yds5: number
  recv_yds10: number
  recv_yds20: number
  recv_yds25: number
  recv_yds50: number
  recv_yds100: number
  recv_receptions1: number
  recv_receptions5: number
  recv_receptions10: number
  pass_2pt: number
  rush_2pt: number
  recv_2pt: number
  pass_td_40plus: number
  pass_td_50plus: number
  rush_td_40plus: number
  rush_td_50plus: number
  recv_td_40plus: number
  recv_td_50plus: number
  fumbles_lost: number
  fg_made_50plus: number
  fg_att_50plus: number
  fg_made_40_49: number
  fg_att_40_49: number
  fg_made_0_19: number
  fg_att_0_19: number
  pat_made: number
  pat_att: number
  dst_kick_td: number
  dst_punt_td: number
  dst_int_td: number
  dst_fr_td: number
  dst_blk_td: number
  dst_int: number
  dst_fumbles_recovered: number
  dst_blocks: number
  dst_safeties: number
  dst_sacks: number
  dst_pts_0: number
  dst_pts_1_6: number
  dst_pts_7_13: number
  dst_pts_14_17: number
  dst_pts_18_21: number
  dst_pts_28_34: number
  dst_pts_35_45: number
  dst_pts_46plus: number
  pass_comp1: number
  pass_comp5: number
  pass_comp10: number
  pass_inc1: number
  pass_inc5: number
  pass_inc10: number
  pass_yds_300plus: number
  pass_yds_400plus: number
  pass_sacked: number
  pass_att: number
  rush_att1: number
  rush_att5: number
  rush_att10: number
  rush_yds_100plus: number
  rush_yds_200plus: number
  recv_yds_100plus: number
  recv_yds_200plus: number
  recv_target: number
  kick_ret_yds1: number
  kick_ret_yds10: number
  kick_ret_yds25: number
  punt_ret_yds1: number
  punt_ret_yds10: number
  punt_ret_yds25: number
  kick_ret_td: number
  punt_ret_td: number
  off_fumble_td: number
  fumbles: number
  fg_made_20_29: number
  fg_made_30_39: number
  fg_att_20_29: number
  fg_att_30_39: number
  pat_miss: number
  fg_miss: number
  fg_miss_0_19: number
  fg_miss_20_29: number
  fg_miss_30_39: number
  fg_miss_40_49: number
  fg_miss_50plus: number
  dst_kick_ret_yds: number
  dst_punt_ret_yds: number
  dst_tackles: number
  dst_fumbles_forced: number
  dst_tackles_for_loss: number
  dst_pass_defended: number
  dst_points_allowed1: number
  dst_yds: number
  dst_yds_0_99: number
  dst_yds_100_199: number
  dst_yds_200_299: number
  dst_yds_300_349: number
  dst_yds_350_399: number
  dst_yds_400_449: number
  dst_yds_450_499: number
  dst_yds_500_549: number
  dst_yds_550plus: number
  dst_pts_22_27: number
  dst_pts_14_20: number
  dst_pts_21_27: number
  pass_int_td: number
  rush_att_40plus: number
  recv_reception_40plus: number
  dst_4th_downs: number
  dst_3_and_outs: number
  dst_yds_neg: number
  dst_2pt: number
  dst_conversion_safeties: number
  idp_return_yds: number
  idp_int_td: number
  idp_fr_td: number
  idp_blk_td: number
  idp_fumbles_forced: number
  idp_fumbles_recovered: number
  idp_blocks: number
  idp_tackles_solo: number
  idp_tackles_assist: number
  idp_tackles_for_loss: number
  idp_sacks: number
  idp_safeties: number
  idp_int: number
  idp_pass_defended: number
  idp_tackles_total: number
  st_fumbles_recovered: number
  st_fumbles_forced: number
  def_int_td: number
  def_fr_td: number
  def_blk_td: number
  def_kick_td: number
  def_punt_td: number
}

export interface LeagueSettings extends LeagueSettingsPositionSettings, LeagueSettingsScoringSettings {
  id: number
  type: string
  name: string | null
}

export interface PositionsUsage {
  [key: string]: {
    starting: number
    max: number
  }
}

export interface DraftingProgress {
  saved_draft: SavedDraft
  drafters: Drafters[]
  picks: number[][]
  roster_spots: number
  positions_usage: PositionsUsage
}

export interface DraftersProgress extends Pick<DraftingProgress, 'saved_draft' | 'drafters'> {}

export interface LeagueSettingsUpsert
  extends LeagueSettingsPositionSettings,
    LeagueSettingsScoringSettings,
    LeagueSettingsPositionLimitSettings {}

export type LeagueSettingsUpsertKeys = keyof LeagueSettingsUpsert

export interface LeagueSettingsUpsertError {
  error: string
  errors: {
    [key: string]: string[]
  }
}

export enum PositionLimits {
  QB = 'limit_qb',
  RB = 'limit_rb',
  WR = 'limit_wr',
  TE = 'limit_te',
  K = 'limit_k',
  DST = 'limit_dst',
}
